import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, ArrowLeft, FileText, Calculator, TrendingUp, Shield, Clock, Users, Star, Search, Receipt } from 'lucide-react';

const PricingPage = () => {
  const features = {
    basic: [
      {
        icon: <FileText className="w-5 h-5" />,
        title: "Unlimited Professional Invoices",
        description: "Create unlimited invoices with your branding and professional formatting"
      },
      {
        icon: <Users className="w-5 h-5" />,
        title: "Multi-Practice Management",
        description: "Manage invoices for multiple dental practices from one dashboard"
      },
      {
        icon: <FileText className="w-5 h-5" />,
        title: "PDF Generation & Export",
        description: "Generate professional PDF invoices that can be downloaded and shared"
      },
      {
        icon: <Clock className="w-5 h-5" />,
        title: "Monthly Invoices",
        description: "Create monthly invoices"
      },
      {
        icon: <Shield className="w-5 h-5" />,
        title: "Secure Data Storage",
        description: "Your financial and practice data stored securely in the cloud"
      },
      {
        icon: <Search className="w-5 h-5" />,
        title: "Location-Based Job Search",
        description: "Find dental job opportunities within your preferred travel distance"
      }
    ],
    pro: [
      {
        icon: <Star className="w-5 h-5" />,
        title: "Everything in Basic",
        description: "All Basic plan features included"
      },
      {
        icon: <Clock className="w-5 h-5" />,
        title: "Daily Work Log",
        description: "Track daily work activities, hours, and earnings efficiently"
      },
      {
        icon: <Receipt className="w-5 h-5" />,
        title: "Smart Expense Tracking",
        description: "Use Smart Camera to scan receipts and track expenses automatically"
      },
      {
        icon: <TrendingUp className="w-5 h-5" />,
        title: "Revenue Reports & Analytics",
        description: "Visual breakdown of income, expenses, and earnings over time"
      },
      {
        icon: <FileText className="w-5 h-5" />,
        title: "Invoice History & Payment Tracking",
        description: "Complete searchable history with payment status tracking"
      },
      {
        icon: <Calculator className="w-5 h-5" />,
        title: "Tax Estimation Calculator",
        description: "Real-time tax liability estimates based on your income and expenses"
      }
    ]
  };

  const plans = [
    {
      id: 'basic',
      name: 'Basic Plan',
      price: 25,
      description: 'Perfect for getting started with professional invoicing',
      features: features.basic,
      popular: false,
      trialText: '60-day free trial + 12 months'
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: 50,
      description: 'Complete solution with expense tracking and detailed reporting',
      features: features.pro,
      popular: true,
      trialText: '60-day free trial + 12 months'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pricing Plans - Dental Professional Invoice & Expense Management | Coooly</title>
        <meta 
          name="description" 
          content="Affordable pricing plans for dental hygienists, therapists, nurses, and locums. Invoice management, expense tracking, and tax estimation starting from £25/year. 60-day free trial included." 
        />
        <meta 
          name="keywords" 
          content="dental invoicing pricing, dental professional software cost, hygienist invoice app price, dental expense tracking pricing, locum dental software, dental therapist billing" 
        />
        <link rel="canonical" href={`${window.location.origin}/pricing`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Pricing Plans - Dental Professional Invoice Management" />
        <meta property="og:description" content="Affordable pricing for dental professionals. Invoice management and expense tracking starting from £25/year." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/pricing`} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Coooly Dental Professional Software",
            "description": "Invoice management and expense tracking software for dental professionals",
            "provider": {
              "@type": "Organization",
              "name": "Coooly"
            },
            "offers": [
              {
                "@type": "Offer",
                "name": "Basic Plan",
                "price": "25",
                "priceCurrency": "GBP",
                "billingIncrement": "P1Y",
                "description": "Invoice management for dental professionals"
              },
              {
                "@type": "Offer",
                "name": "Pro Plan", 
                "price": "50",
                "priceCurrency": "GBP",
                "billingIncrement": "P1Y",
                "description": "Complete invoice and expense management solution"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white">
          <div className="container mx-auto px-4 pt-12 pb-10">
            <div className="flex items-center justify-between mb-8">
              <Link 
                to="/"
                className="flex items-center gap-2 text-white hover:text-purple-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Home
              </Link>
              <Link to="/blog" className="text-white hover:text-purple-100 transition-colors">
                Blog
              </Link>
            </div>
            
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-5xl md:text-6xl font-bold mb-10 bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent" style={{paddingBottom: '0.5rem'}}>
                Simple, Transparent Pricing
              </h1>
              <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed mb-2">
                Choose the perfect plan for your freelance dental career. Built for self-employed dental professionals working across multiple practices.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="container mx-auto px-4 pt-16 pb-16">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden bg-white border border-gray-200 shadow-lg flex flex-col ${plan.popular ? 'ring-2 ring-purple-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <CardHeader className={plan.popular ? 'pt-12 flex-grow' : 'pt-8 flex-grow'}>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </CardTitle>
                  <p className="text-gray-700 mb-4">{plan.description}</p>
                  <div className="mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-gray-900">
                        £{plan.price}
                      </span>
                      <span className="text-gray-700">
                        /year
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      (£{(plan.price / 12).toFixed(2)} / month)
                    </p>
                  </div>
                  <p className="text-sm text-purple-600 font-medium">
                    {plan.trialText}
                  </p>
                </CardHeader>
                <CardContent className="pb-8 flex flex-col flex-grow">
                  <div className="space-y-4 mb-8 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="text-purple-600 mt-0.5">
                          {feature.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">
                            {feature.title}
                          </h4>
                          <p className="text-sm text-gray-700">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-auto">
                    <Button 
                      asChild
                      className={`w-full py-6 text-lg font-semibold ${
                        plan.popular
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                          : 'bg-purple-600 hover:bg-purple-700'
                      } text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all`}
                    >
                      <Link to="/">
                        Start {plan.name} Trial
                      </Link>
                    </Button>
                    <p className="text-center text-sm text-gray-500 mt-4">
                      No credit card required for trial
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Who is this software for?
                </h3>
                <p className="text-gray-600 mb-6">
                  Self-employed dental professionals who work across multiple practices including hygienists, 
                  dental therapists, dental nurses, and locum dentists who need professional invoicing and expense tracking.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  What's included in the free trial?
                </h3>
                <p className="text-gray-600 mb-6">
                  Full access to all features of your chosen plan for 60 days. No credit card required to start, 
                  and you can cancel anytime during the trial period.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Can I switch plans later?
                </h3>
                <p className="text-gray-600 mb-6">
                  Yes, you can upgrade or downgrade your plan at any time through the billing section. 
                  Changes will be reflected in your next billing cycle.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  How does invoicing work?
                </h3>
                <p className="text-gray-600 mb-6">
                  Create professional PDF invoices with your details pre-filled. You can download and send them 
                  to the practices where you work, with full payment tracking included.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to streamline your freelance dental career?
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Join thousands of dental professionals who use our platform to manage their invoicing and track expenses across multiple practices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                className="px-8 py-4 bg-white text-purple-600 hover:bg-gray-100 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                <Link to="/">
                  Start Free Trial
                </Link>
              </Button>
              <Button 
                asChild
                className="px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white hover:text-purple-600 text-lg font-semibold transition-all transform hover:scale-105"
              >
                <Link to="/features">
                  Learn More About Features
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingPage;
